
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useOrcamentosPaginated, useOrcamentosCount, useCriarOrcamento, useAprovarOrcamento, useExcluirOrcamento, useInativarOrcamentoVencido, useRejeitarOrcamentoVistoria, type OrcamentosFiltroParams, type OrcamentosSort } from "@/hooks/useOrcamentos";
import { usePlanosAtivos } from "@/hooks/usePlanos";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";
import { numeroParcelasAoSelecionarForma } from "@/lib/orcamentoFormaPagamento";
import { isVendedorScopedRole } from "@/types/auth";
import { OrcamentoCalculator } from "@/components/orcamentos/OrcamentoCalculator";
import { OrcamentoClienteForm } from "@/components/orcamentos/OrcamentoClienteForm";
import { OrcamentosList } from "@/components/orcamentos/OrcamentosList";
import { EquipamentosSelector } from "@/components/orcamentos/EquipamentosSelector";
import { gerarPdfOrcamento } from "@/utils/gerarPdfOrcamento";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export function Orcamentos() {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const filtrosDraftDefault = {
    dataInicio: "",
    dataFim: "",
    planoId: "",
    status: "",
    estado: "",
    cidade: "",
    valorProjetoMin: "",
    valorProjetoMax: "",
    valorSeguroMin: "",
    valorSeguroMax: "",
    paineisMin: "",
    paineisMax: "",
  };
  const [filtrosDraft, setFiltrosDraft] = useState(filtrosDraftDefault);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosDraftDefault);
  const [sort, setSort] = useState<OrcamentosSort>(() => ({ field: "created_at", direction: "desc" }));
  
  const [qtdPaineis, setQtdPaineis] = useState(0);
  const [planoSelecionado, setPlanoSelecionado] = useState("");
  const [valorPersonalizado, setValorPersonalizado] = useState(20);
  const [vigencia, setVigencia] = useState("1");
  const [tipoCliente, setTipoCliente] = useState("existente");
  const [clienteSelecionado, setClienteSelecionado] = useState("");
  const [clienteNomeSelecionado, setClienteNomeSelecionado] = useState("");
  const [formaPagamento, setFormaPagamento] = useState("");
  const [parcelasCartao, setParcelasCartao] = useState("1");
  const [valorFinalProposta, setValorFinalProposta] = useState<number | null>(null);
  const [dadosCliente, setDadosCliente] = useState({
    nome: "",
    email: "",
    telefone: "",
    observacoes: ""
  });
  
  // Estados para controle de modais
  const [showCalculator, setShowCalculator] = useState(false);
  const [showClienteForm, setShowClienteForm] = useState(false);
  const [activeTab, setActiveTab] = useState("lista");
  
  // Verificar se há projeto para criar orçamento
  useEffect(() => {
    const handleCriarOrcamentoProjeto = () => {
      const projetoData = localStorage.getItem('projeto_para_orcamento');
      if (projetoData) {
        const projeto = JSON.parse(projetoData);
        setTipoCliente('existente');
        setClienteSelecionado(projeto.cliente_id.toString());
        setClienteNomeSelecionado(projeto.cliente_nome);
        setShowClienteForm(true);
        localStorage.removeItem('projeto_para_orcamento');
        toast.success(`Criando orçamento para ${projeto.cliente_nome}`);
      }
    };
    
    window.addEventListener('criar_orcamento_projeto', handleCriarOrcamentoProjeto);
    handleCriarOrcamentoProjeto(); // Verificar na montagem
    
    return () => window.removeEventListener('criar_orcamento_projeto', handleCriarOrcamentoProjeto);
  }, []);

  const [buscaOrcamentoInput, setBuscaOrcamentoInput] = useState(""); // Estado local para o input
  const [buscaOrcamento, setBuscaOrcamento] = useState(""); // Estado para a query (com debounce)
  
  // Debounce: atualizar busca após 700ms sem digitação (reduz significativamente requisições ao banco)
  useEffect(() => {
    // Se o campo estiver vazio, limpar busca imediatamente
    if (buscaOrcamentoInput === "") {
      setBuscaOrcamento("");
      return;
    }

    // Para campos com texto, aguardar delay para evitar queries excessivas
    const timer = setTimeout(() => {
      setBuscaOrcamento(buscaOrcamentoInput);
    }, 700);

    return () => clearTimeout(timer);
  }, [buscaOrcamentoInput]);
  
  const filtrosHook: OrcamentosFiltroParams = {
    periodo:
      filtrosAplicados.dataInicio || filtrosAplicados.dataFim
        ? {
            dataInicio: filtrosAplicados.dataInicio,
            dataFim: filtrosAplicados.dataFim,
          }
        : null,
    planoId: filtrosAplicados.planoId ? filtrosAplicados.planoId : null,
    status: filtrosAplicados.status ? filtrosAplicados.status : null,
    estado: filtrosAplicados.estado ? filtrosAplicados.estado : null,
    cidade: filtrosAplicados.cidade ? filtrosAplicados.cidade : null,
    valorProjeto:
      filtrosAplicados.valorProjetoMin || filtrosAplicados.valorProjetoMax
        ? {
            min: filtrosAplicados.valorProjetoMin ? Number(filtrosAplicados.valorProjetoMin) : undefined,
            max: filtrosAplicados.valorProjetoMax ? Number(filtrosAplicados.valorProjetoMax) : undefined,
          }
        : null,
    valorSeguro:
      filtrosAplicados.valorSeguroMin || filtrosAplicados.valorSeguroMax
        ? {
            min: filtrosAplicados.valorSeguroMin ? Number(filtrosAplicados.valorSeguroMin) : undefined,
            max: filtrosAplicados.valorSeguroMax ? Number(filtrosAplicados.valorSeguroMax) : undefined,
          }
        : null,
    paineis:
      filtrosAplicados.paineisMin || filtrosAplicados.paineisMax
        ? {
            min: filtrosAplicados.paineisMin ? Number(filtrosAplicados.paineisMin) : undefined,
            max: filtrosAplicados.paineisMax ? Number(filtrosAplicados.paineisMax) : undefined,
          }
        : null,
  };

  // Buscar orçamentos paginados com busca + filtros aplicados
  const { data: orcamentosResult, isLoading: loadingOrcamentos, error: orcamentosError } = useOrcamentosPaginated(
    currentPage,
    itemsPerPage,
    buscaOrcamento,
    filtrosHook,
    sort
  );
  const orcamentos = orcamentosResult?.data || [];
  const totalOrcamentos = orcamentosResult?.count || 0;
  const { data: planosAtivos } = usePlanosAtivos();
  const { data: formasPagamentoAtivas } = useFormasPagamentoAtivas();
  const { profile, user } = useAuth();
  const criarOrcamento = useCriarOrcamento();
  const aprovarOrcamento = useAprovarOrcamento();
  const excluirOrcamento = useExcluirOrcamento();
  const rejeitarOrcamentoVistoria = useRejeitarOrcamentoVistoria();

  // Logs para debug
  console.log('Orcamentos hook - data:', orcamentos);
  console.log('Orcamentos hook - loading:', loadingOrcamentos);
  console.log('Orcamentos hook - error:', orcamentosError);

  // Selecionar o primeiro plano ativo por padrão
  if (planosAtivos && planosAtivos.length > 0 && !planoSelecionado) {
    setPlanoSelecionado(planosAtivos[0].id);
  }

  // Resetar para página 1 quando busca mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [buscaOrcamento, sort.field, sort.direction]);

  useEffect(() => {
    if (formasPagamentoAtivas?.length && !formaPagamento) {
      const padrao =
        formasPagamentoAtivas.find((f) => f.tipo === "pix") ?? formasPagamentoAtivas[0];
      setFormaPagamento(padrao.id.toString());
    }
  }, [formasPagamentoAtivas, formaPagamento]);

  const getFormaPagamentoTipo = () => {
    const forma = formasPagamentoAtivas?.find(
      (f) => f.id.toString() === formaPagamento || f.tipo === formaPagamento
    );
    return forma?.tipo ?? formaPagamento;
  };

  const toggleSort = (field: OrcamentosSort["field"]) => {
    setSort((prev) => {
      if (prev.field === field) return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { field, direction: "asc" };
    });
  };

  const handleAplicarFiltros = () => {
    setFiltrosAplicados(filtrosDraft);
    setCurrentPage(1);
  };

  const handleLimparFiltros = () => {
    setFiltrosDraft(filtrosDraftDefault);
    setFiltrosAplicados(filtrosDraftDefault);
    setCurrentPage(1);
  };

  const calcularValorBase = () => {
    // Buscar o plano selecionado nos planos ativos
    const planoEncontrado = planosAtivos?.find(plano => plano.id === planoSelecionado);
    
    let valorPorPainel = 15; // valor padrão
    
    if (planoEncontrado?.valor_por_painel) {
      valorPorPainel = planoEncontrado.valor_por_painel;
    } else if (planoSelecionado === "personalizado") {
      valorPorPainel = valorPersonalizado;
    }
    
    return qtdPaineis * valorPorPainel;
  };

  const calcularValorTabela = () => {
    const valorBase = calcularValorBase();
    return vigencia === "2" ? valorBase * 1.8 : valorBase;
  };

  const calcularJuros = (valor: number) => {
    const tipo = getFormaPagamentoTipo();
    if (tipo === "pix" || tipo === "boleto-avista") {
      return 0;
    }
    
    if (tipo === "boleto-parcelado") {
      const parcelas = parseInt(vigencia);
      return valor * (parcelas - 1) * 0.02;
    }
    
    if (tipo === "cartao") {
      const parcelas = parseInt(parcelasCartao);
      return valor * (parcelas - 1) * 0.01;
    }
    
    return 0;
  };

  const calcularOrcamento = () => {
    const valorTabela = calcularValorTabela();
    const valorComercial =
      valorFinalProposta != null && valorFinalProposta > 0 ? valorFinalProposta : valorTabela;
    const juros = calcularJuros(valorComercial);
    return valorComercial + juros;
  };

  // Quando painéis/plano/vigência mudam, resetar valor final para a nova tabela
  useEffect(() => {
    setValorFinalProposta(calcularValorTabela());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtdPaineis, planoSelecionado, valorPersonalizado, vigencia, planosAtivos]);

  const gerarOrcamento = async () => {
    console.log('Iniciando geração de orçamento...');
    console.log('Cliente selecionado:', clienteSelecionado);
    console.log('Quantidade painéis:', qtdPaineis);
    
    if (!qtdPaineis || qtdPaineis <= 0) {
      toast.error("Informe a quantidade de painéis");
      return;
    }

    if (tipoCliente === "existente" && !clienteSelecionado) {
      toast.error("Selecione um cliente");
      return;
    }

    if (tipoCliente === "existente" && !clienteSelecionado.trim()) {
      toast.error("Selecione um cliente válido");
      return;
    }

    try {
      let clienteId: number;
      
      if (tipoCliente === "existente") {
        const clienteIdNum = parseInt(clienteSelecionado);
        if (isNaN(clienteIdNum)) {
          toast.error("ID do cliente inválido");
          return;
        }
        clienteId = clienteIdNum;
        console.log('Cliente ID convertido:', clienteId);
      } else {
        toast.error("Funcionalidade de cliente teste será implementada em breve");
        return;
      }

      const valorTabela = calcularValorTabela();
      const valorComercial =
        valorFinalProposta != null && valorFinalProposta > 0 ? valorFinalProposta : valorTabela;

      if (valorComercial > valorTabela + 0.009) {
        toast.error("O valor final não pode ser maior que o valor de tabela");
        return;
      }

      const descontoValor = Math.max(0, Math.round((valorTabela - valorComercial) * 100) / 100);
      const juros = calcularJuros(valorComercial);
      const valorFinal = valorComercial + juros;

      const formaNome =
        formasPagamentoAtivas?.find((f) => f.id.toString() === formaPagamento)?.nome ??
        formaPagamento;

      // Criar observações estruturadas
      const observacoes = [
        dadosCliente.observacoes,
        `Quantidade de painéis: ${qtdPaineis}`,
        `Vigência: ${vigencia} ano${vigencia === "2" ? 's' : ''}`,
        `Forma de pagamento: ${formaNome}`,
        `Valor de tabela: R$ ${valorTabela.toFixed(2)}`,
        descontoValor > 0 ? `Desconto comercial: R$ ${descontoValor.toFixed(2)}` : null,
        `Valor final: R$ ${valorFinal.toFixed(2)}`
      ].filter(Boolean).join(' | ');

      console.log('Dados para criar orçamento:', {
        cliente_id: clienteId,
        valor_total: valorFinal,
        observacoes: observacoes,
        plano_id: planoSelecionado
      });

      const parcelas = numeroParcelasAoSelecionarForma(
        formaPagamento,
        formasPagamentoAtivas ?? [],
        parseInt(parcelasCartao, 10)
      );

      const result = await criarOrcamento.mutateAsync({
        cliente_id: clienteId,
        valor_total: valorFinal,
        valor_seguro: valorComercial,
        quantidade_placas: qtdPaineis,
        vigencia_anos: parseInt(vigencia, 10) || 1,
        observacoes: observacoes,
        plano_id: planoSelecionado === "personalizado" ? undefined : planoSelecionado,
        forma_pagamento: formaPagamento || null,
        parcelas_cartao: parcelas,
        numero_parcelas: parcelas,
        valor_tabela: valorTabela,
        desconto_valor: descontoValor,
      });

      console.log('Orçamento criado com sucesso:', result);
      toast.success("Orçamento criado com sucesso!");
      
      // Limpar form
      setQtdPaineis(0);
      setClienteSelecionado("");
      setClienteNomeSelecionado("");
      setValorPersonalizado(20);
      setValorFinalProposta(null);
      setDadosCliente({
        nome: "",
        email: "",
        telefone: "",
        observacoes: ""
      });
    } catch (error) {
      console.error('Erro ao criar orçamento:', error);
      toast.error("Erro ao criar orçamento: " + (error as any)?.message);
    }
  };

  const handleAprovarOrcamento = async (orcamentoId: number) => {
    console.log('🎯 Iniciando aprovação do orçamento:', orcamentoId);
    try {
      const result = await aprovarOrcamento.mutateAsync(orcamentoId);
      console.log('✅ Resultado da aprovação:', result);
      toast.success("Orçamento aprovado! Projeto e pedido criados automaticamente. Contrato e financeiro devem ser criados manualmente através dos botões na listagem de pedidos.");
    } catch (error) {
      console.error('❌ Erro ao aprovar orçamento:', error);
      let errorMessage = "Erro ao aprovar orçamento";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as any).message;
      }
      toast.error(`Erro ao aprovar orçamento: ${errorMessage}`);
    }
  };

  const handleRejeitarOrcamentoVistoria = async (orcamentoId: number, motivo?: string) => {
    try {
      await rejeitarOrcamentoVistoria.mutateAsync({ orcamentoId, motivo });
      toast.success("Orçamento rejeitado com base na vistoria.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao rejeitar orçamento.");
    }
  };

  const handleClienteChange = (clienteId: string, clienteNome: string) => {
    console.log('Cliente selecionado:', clienteId, clienteNome);
    setClienteSelecionado(clienteId);
    setClienteNomeSelecionado(clienteNome);
  };

  const handleGerarPdf = async (orcamento: any) => {
    try {
      await gerarPdfOrcamento(orcamento);
      toast.success("PDF do orçamento gerado com sucesso!");
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(
        error instanceof Error ? error.message : "Erro ao gerar PDF do orçamento"
      );
    }
  };

  const handleExcluirOrcamento = async (orcamentoId: number) => {
    try {
      await excluirOrcamento.mutateAsync(orcamentoId);
      toast.success("Orçamento excluído com sucesso!");
    } catch (error: any) {
      console.error('Erro ao excluir orçamento:', error);
      toast.error(error.message || "Erro ao excluir orçamento");
    }
  };

  const handleCreateOrcamento = () => {
    setActiveTab("novo");
  };

  // Os orçamentos já vêm filtrados do backend
  const orcamentosFiltrados = orcamentos;

  // Calcular dados da paginação
  const totalItems = totalOrcamentos;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = orcamentosFiltrados; // Já vem paginado e filtrado do backend

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Orçamentos</h2>
          <p className="text-gray-600">Gere e gerencie orçamentos personalizados para seus clientes</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="novo">Novo Orçamento</TabsTrigger>
          <TabsTrigger value="lista">Lista de Orçamentos ({totalOrcamentos})</TabsTrigger>
        </TabsList>

        <TabsContent value="novo" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <OrcamentoCalculator
              qtdPaineis={qtdPaineis}
              setQtdPaineis={setQtdPaineis}
              planoSelecionado={planoSelecionado}
              setPlanoSelecionado={setPlanoSelecionado}
              valorPersonalizado={valorPersonalizado}
              setValorPersonalizado={setValorPersonalizado}
              vigencia={vigencia}
              setVigencia={setVigencia}
              formaPagamento={formaPagamento}
              setFormaPagamento={setFormaPagamento}
              parcelasCartao={parcelasCartao}
              setParcelasCartao={setParcelasCartao}
              valorTabela={calcularValorTabela()}
              valorFinalProposta={valorFinalProposta ?? calcularValorTabela()}
              setValorFinalProposta={setValorFinalProposta}
              calcularOrcamento={calcularOrcamento}
              calcularValorBase={calcularValorBase}
              calcularJuros={calcularJuros}
            />

            <OrcamentoClienteForm
              tipoCliente={tipoCliente}
              setTipoCliente={setTipoCliente}
              clienteSelecionado={clienteSelecionado}
              clienteNomeSelecionado={clienteNomeSelecionado}
              handleClienteChange={handleClienteChange}
              dadosCliente={dadosCliente}
              setDadosCliente={setDadosCliente}
              gerarOrcamento={gerarOrcamento}
              qtdPaineis={qtdPaineis}
              isPending={criarOrcamento.isPending}
              orcamentoId={undefined} // Para novo orçamento
              onProjetoSelect={(projetoId, totalPaineis) => {
                if (totalPaineis) {
                  setQtdPaineis(totalPaineis);
                }
                toast.success(`Projeto vinculado! ${totalPaineis || 0} painéis encontrados.`);
              }}
            />
          </div>
        </TabsContent>

        <TabsContent value="lista" className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Orçamentos pendentes têm prazo de 30 dias para aprovação
            </div>
          </div>
          
          <OrcamentosList
            orcamentosFiltrados={currentItems}
            loadingOrcamentos={loadingOrcamentos}
            buscaOrcamento={buscaOrcamentoInput}
            setBuscaOrcamento={setBuscaOrcamentoInput}
            buscaOrcamentoQuery={buscaOrcamento}
            filtrosDraft={filtrosDraft}
            setFiltrosDraft={setFiltrosDraft}
            filtrosAplicados={filtrosAplicados}
            onApplyFilters={handleAplicarFiltros}
            onClearFilters={handleLimparFiltros}
            currentUserId={user?.id ?? null}
            sort={sort}
            onToggleSort={toggleSort}
            handleGerarPdf={handleGerarPdf}
            handleAprovarOrcamento={handleAprovarOrcamento}
            handleExcluirOrcamento={handleExcluirOrcamento}
            handleRejeitarOrcamentoVistoria={handleRejeitarOrcamentoVistoria}
            isPendingAprovacao={aprovarOrcamento.isPending}
            isPendingRejeicao={rejeitarOrcamentoVistoria.isPending}
            isVendedor={isVendedorScopedRole(profile?.role)}
            orcamentos={orcamentos}
            planos={planosAtivos}
            onCreateOrcamento={handleCreateOrcamento}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
